
// Types
import Vue, { PropType } from 'vue';
import { WordCloud } from './interfaces';
import { LAYOUT_SET, Layouts } from './constants';
import { WordCloudSvgType } from './svg-canvas/word-cloud-svg';

// Dependencies
import { isEqual, cloneDeep, throttle } from 'lodash';

// Components
import WordCloudSvg from './svg-canvas/WordCloudSvg.vue';

export default Vue.extend({
    name: 'WordCloud',

    components: {
        WordCloudSvg,
    },

    props: {
        words: {
            type: Array as PropType<WordCloud.Word[]>,
            required: true,
        },
        config: {
            type: Object as PropType<Partial<WordCloud.Config>>,
            required: true,
        }
    },

    data() {
        return {
            resizeObserver: null as ResizeObserver | null,
            width: 0,
            height: 0,
            rpx: 1,
            currentWords: [] as WordCloud.Word[],
            onResizeThrottled: () => {}
        };
    },

    computed: {
        preparedConfig(): WordCloud.Config {
            const isLayoutSet = this.config.hasOwnProperty('layout');
            const large = !!this.config?.large;

            const layout = isLayoutSet && this.config.layout
                ? this.config.layout
                : (large ? Layouts.Display : Layouts.Compact);

            const colors = (Object.keys(this.config?.colors || {}).length !== 0)
                ? this.config.colors as WordCloud.Colors
                : {
                    colour_1: '#0000',
                    colour_2: '#0000',
                    colour_3: '#0000',
                };

            return {
                font: {
                    sizeRange: LAYOUT_SET[layout].SIZE_RANGE,
                    family: 'sans-serif',
                    weight: 'normal',
                    ...(this.config?.font || {}),
                },
                large: isLayoutSet ? this.config.layout === Layouts.Display : large,
                layout,
                colors,
                labels: this.config?.labels || {
                    wordCloudEmpty: 'No words yet',
                },
            };
        }
    },

    watch: {
        config: {
            handler(newVal, oldVal) {
                if (isEqual(newVal, oldVal)) {
                    return;
                }
                console.debug('[WordCloud]: config changed', newVal);
                this.setRpx(newVal);
                this.onResize();
            },
            immediate: true,
            deep: true,
        },

        width(newVal, oldVal) {
            if (isEqual(newVal, oldVal)) {
                return;
            }
            this.redraw();
        },

        height(newVal, oldVal) {
            if (isEqual(newVal, oldVal)) {
                return;
            }
            this.redraw();
        },

        words: {
            immediate: true,
            deep: true,
            handler(next) {
                const preparedNext = this.prepareWords(next || []);
                const preparedPrev = this.prepareWords(this.currentWords || []);
                this.currentWords = this.prepareWords(next || []);
                if (!isEqual(preparedNext, preparedPrev)) {
                    console.debug('[WordCloud]: words changed', preparedNext);
                    this.currentWords = preparedNext;
                    this.debouncedRedraw();
                }
            }
        }
    },

    created() {
        this.onResizeThrottled = throttle(this.onResize.bind(this), 500);
    },

    mounted() {
        this.width = this.$el.clientWidth;
        this.height = this.$el.clientHeight;
        this.resizeObserver = new ResizeObserver(this.onResizeThrottled);
        this.resizeObserver.observe(this.$el);
        this.resizeObserver.observe(window.document.body);
        this.setRpx(this.config);
    },

    beforeDestroy() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    },

    methods: {
        prepareWords(words: WordCloud.Word[]): WordCloud.Word[] {
            return cloneDeep(words)
                .sort((a, b) => b.votes - a.votes)
                .slice(0, LAYOUT_SET[this.preparedConfig.layout as string].MAX_WORDS);
        },

        async handleRedraw(handler: 'changeHandler' | 'debouncedHandler') {
            const canvas = this.$refs?.canvas as InstanceType<WordCloudSvgType> | undefined;
            if (canvas) {
                canvas.$forceUpdate();
                canvas.refit();
                await this.$nextTick();
                canvas[handler]();
            }
        },

        redraw() {
            this.handleRedraw('changeHandler');
        },

        debouncedRedraw() {
            this.handleRedraw('debouncedHandler');
        },

        onResize() {
            if (!this.$el) return;

            const width = this.$el.clientWidth;

            this.width = this.$el.clientWidth;
            this.height = this.$el.clientHeight;

            if (this.setRpx(this.config)) {
                this.redraw();
            } else if (this.width !== width) {
                this.redraw();
            }
        },

        setRpx(config: Partial<WordCloud.Config>): boolean {
            let redraw = false;

            const rpx = config.large
                ? this.getRpx()
                : 1;

            if (this.rpx !== rpx) {
                this.rpx = rpx;
                redraw = true;
            }

            return redraw;
        },

        getRpx(): number {
            if (!this.$el) return 1;
            this.width = this.$el.clientWidth;
            this.height = this.$el.clientHeight;
            return this.width >= this.height
                ? this.width / 1216
                : this.height / 1216;
        },
    }
});
